import { useCallback, useMemo, useState } from 'react'
import unitsData from './data/units.json'
import { getCopy, localizeData } from './i18n.js'
import Calculator from './components/Calculator.jsx'
import AdBanner from './components/AdBanner.jsx'
import Footer from './components/Footer.jsx'
import Header from './components/Header.jsx'
import Hero from './components/Hero.jsx'
import HistoricalSection from './components/HistoricalSection.jsx'
import SeoHead from './components/SeoHead.jsx'
import SystemsSection from './components/SystemsSection.jsx'
import UnitExplainers from './components/UnitExplainers.jsx'
import { detectLocale } from './lib/localeUrls.js'

export default function App() {
  const [locale] = useState(detectLocale)
  const localizedData = useMemo(() => localizeData(unitsData, locale), [locale])
  const copy = getCopy(locale)
  const [selectedCategoryId, setSelectedCategoryId] = useState('length')
  const [selection, setSelection] = useState(() => {
    const category = unitsData.categories[0]
    return {
      category,
      fromUnit: category.units.find((unit) => unit.id === category.defaultFrom),
      toUnit: category.units.find((unit) => unit.id === category.defaultTo),
    }
  })
  const [menuOpen, setMenuOpen] = useState(false)
  const [requestedUnit, setRequestedUnit] = useState(null)
  const [selectedSystemStoryId, setSelectedSystemStoryId] = useState(null)

  const handleSelectionChange = useCallback((nextSelection) => setSelection(nextSelection), [])
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  const closeSystemStory = useCallback(() => setSelectedSystemStoryId(null), [])
  function selectHistoricalUnit(categoryId, unitId) {
    const category = localizedData.categories.find((item) => item.id === categoryId)
    const metricUnit = category.units.find((unit) => unit.id === category.baseUnit)
      ?? category.units.find((unit) => unit.system === 'metric')
    setSelectedCategoryId(categoryId)
    setRequestedUnit({ categoryId, unitId, toUnitId: metricUnit.id, requestId: Date.now() })
    window.requestAnimationFrame(() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  return (
    <div id="top">
      <SeoHead locale={locale} />
      <a className="skip-link" href="#main-content">{copy.skipToContent}</a>
      <Header locale={locale} copy={copy} menuOpen={menuOpen} onMenuToggle={() => menuOpen ? closeMenu() : setMenuOpen(true)} onMenuClose={closeMenu} />
      <main id="main-content">
        <div className="page-shell top-grid">
          <Hero copy={copy} />
          <Calculator
            key={`${locale}-${selectedCategoryId}-${requestedUnit?.requestId ?? 0}`}
            locale={locale}
            copy={copy}
            categories={localizedData.categories}
            historicalNote={localizedData.historicalNote}
            selectedCategoryId={selectedCategoryId}
            onCategoryChange={setSelectedCategoryId}
            onSelectionChange={handleSelectionChange}
            requestedUnit={requestedUnit}
          />
        </div>
        <div className="page-shell lower-content">
          <AdBanner label={copy.advertisement} />
          <HistoricalSection locale={locale} copy={copy} categories={localizedData.categories} note={localizedData.historicalNote} onUseUnit={selectHistoricalUnit} onReadFullHistory={() => setSelectedSystemStoryId('historical')} />
          <UnitExplainers locale={locale} copy={copy} category={selection.category} fromUnit={selection.fromUnit} toUnit={selection.toUnit} />
          <SystemsSection locale={locale} copy={copy} systems={localizedData.systems} selectedSystemId={selectedSystemStoryId} onSelectSystem={setSelectedSystemStoryId} onCloseStory={closeSystemStory} />
        </div>
      </main>
      <Footer copy={copy} />
    </div>
  )
}
